/**
 * Print utilities for admin interface
 */

import { GUN_PACKAGES } from './constants';

/**
 * Get the next order ID for today, incrementing and resetting daily
 * @returns {number} The next order ID
 */
export const getNextOrderId = () => {
  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
  const storageKey = `orderId_${today}`;
  
  // Get current ID from localStorage or default to 0
  const currentId = parseInt(localStorage.getItem(storageKey) || '0', 10);
  const nextId = currentId + 1;
  
  // Save the next ID for subsequent calls
  localStorage.setItem(storageKey, nextId.toString());
  
  return nextId;
};

/**
 * Print receipt content
 * @param {string} content - HTML content to print
 */
export const printReceipt = (content) => {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'absolute';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.visibility = 'hidden';
  
  document.body.appendChild(iframe);
  
  iframe.onload = () => {
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 100);
  };

  iframe.srcdoc = content;
};

/**
 * Generate print receipt HTML for gun orders
 * @param {object} order - Order object
 * @param {string} key - Order key
 * @returns {string} HTML content for printing
 */
export const generateGunOrderReceipt = (order, key) => {
  const formattedDate = new Date(order.timestamp).toLocaleString();
  const orderId = getNextOrderId(); // Get the next order ID
  
  // Process items - include both package names and their items
  let itemsToPrint = [];
  order.items.forEach((item) => {
    if (GUN_PACKAGES[item]) {
      // Item is a package name - add package name and its items
      itemsToPrint.push(`  ${item}`);
      GUN_PACKAGES[item].forEach((pkgItem) => {
        itemsToPrint.push(`   - ${pkgItem}`);
      });
    } else {
      // Regular item - add as-is
      itemsToPrint.push(` • ${item}`);
    }
  });

  const formattedItems = itemsToPrint.join("\n");

  return `
    <html>
      <head>
        <title>Order ${key}</title>
        <style>
          body { font-family: Arial; padding: 5px; }
          h1 { color: #333; }
          .order-info { margin-bottom: 20px; }
          .items-list { margin-top: 20px; }
          .package-item { color: #0066cc; font-weight: bold; }
          .notes { margin-top: 15px; padding: 10px; border-top: 1px dashed #ccc; }
        </style>
      </head>
      <body>
        <h1>${order.customer}</h1>
        <div className="order-info">
          <p><strong>Order ID:</strong> ${orderId}</p>
          <p><strong>Date:</strong> ${formattedDate}</p>
        </div>
        <div className="items-list">
          <pre>${formattedItems}</pre>
        </div>
        <p><strong>Total:</strong> ${new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(order.total)}</p>
        ${order.notes ? `
        <div className="notes">
          <p><strong>Notes:</strong></p>
          <p>${order.notes}</p>
        </div>
        ` : ""}
      </body>
    </html>
  `;
};


/**
 * Extract dog tag CSV data from order
 * @param {object} order - Order object
 * @returns {string|null} CSV data or null if no dog tags
 */
export const extractDogTagCSV = (order) => {
  if (!order?.items || !Array.isArray(order.items)) return null;
  
  const dogTagItem = order.items.find(item => 
    typeof item === 'string' && item.includes('Dog Tag:')
  );
  
  return dogTagItem ? dogTagItem.split('Dog Tag:')[1]?.trim() : null;
};